import { HStack, Icon, VStack } from "@/shared/ui/primitives";
import { platformLogos } from "@/assets/icons/platform";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const platforms = [
	// 메시징
	{ name: "KakaoTalk", category: "messaging" },
	{ name: "Naver", category: "messaging" },
	{ name: "Gmail", category: "messaging" },
	{ name: "Slack", category: "messaging" },
	{ name: "SMS", category: "messaging" },
	{ name: "WhatsApp", category: "messaging" },
	// 결제
	{ name: "Toss", category: "payment" },
	{ name: "Iamport", category: "payment" },
	{ name: "Stripe", category: "payment" },
	{ name: "PayPal", category: "payment" },
	{ name: "KakaoPay", category: "payment" },
	{ name: "NaverPay", category: "payment" },
	{ name: "Bank API", category: "payment" },
	// 커머스
	{ name: "SmartStore", category: "commerce" },
	{ name: "Coupang", category: "commerce" },
	{ name: "Shopify", category: "commerce" },
	{ name: "Cafe24", category: "commerce" },
	{ name: "Amazon", category: "commerce" },
	{ name: "11st", category: "commerce" },
	// 생산성
	{ name: "Sheets", category: "productivity" },
	{ name: "Notion", category: "productivity" },
	{ name: "Airtable", category: "productivity" },
	{ name: "Dropbox", category: "productivity" },
	{ name: "AWS S3", category: "productivity" },
];

const categoryDotColors: Record<string, string> = {
	messaging: "bg-blue-400",
	payment: "bg-violet-400",
	commerce: "bg-emerald-400",
	productivity: "bg-amber-400",
};

// 방사형 배치를 위한 위치 계산
const getOrbitPositions = (count: number, radiusX: number, radiusY: number, offsetAngle = 0) =>
	Array.from({ length: count }, (_, i) => {
		const angle = (2 * Math.PI * i) / count + offsetAngle;
		return { x: Math.cos(angle) * radiusX, y: Math.sin(angle) * radiusY };
	});

export const SolutionSection = () => {
	const { t } = useTranslation();

	const categories = [
		{ key: "messaging", label: t("home.solution.messaging") },
		{ key: "payment", label: t("home.solution.payment") },
		{ key: "commerce", label: t("home.solution.commerce") },
		{ key: "productivity", label: t("home.solution.productivity") },
	];

	// 3개의 궤도로 나눔
	const innerPlatforms = platforms.slice(0, 8);
	const middlePlatforms = platforms.slice(8, 17);
	const outerPlatforms = platforms.slice(17);

	const innerPositions = getOrbitPositions(innerPlatforms.length, 110, 100, -Math.PI / 8);
	const middlePositions = getOrbitPositions(middlePlatforms.length, 185, 170, Math.PI / 9);
	const outerPositions = getOrbitPositions(outerPlatforms.length, 255, 235, 0);

	const allItems = [
		...innerPlatforms.map((p, i) => ({ ...p, ...innerPositions[i], orbit: 0 })),
		...middlePlatforms.map((p, i) => ({ ...p, ...middlePositions[i], orbit: 1 })),
		...outerPlatforms.map((p, i) => ({ ...p, ...outerPositions[i], orbit: 2 })),
	];

	return (
		<VStack className="w-full gap-[28px]">
			<h2 className="text-[1.5rem] md:text-[2.25rem] text-ct-strong font-semibold text-center">
				{t("home.solution.title")}
			</h2>

			<HStack className="w-full gap-[40px] items-center flex-col lg:flex-row">
				{/* 왼쪽: 설명 */}
				<motion.div
					className="w-full lg:w-[360px] shrink-0"
					initial={{ opacity: 0, x: -40 }}
					whileInView={{ opacity: 1, x: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
				>
					<VStack className="gap-[24px] items-start">
						<VStack className="gap-[12px] items-start">
							<HStack className="gap-[8px]">
								<Icon name="Action" width={28} height={28} className="text-pm-strong" />
								<span className="text-sm text-pm-strong font-semibold">
									{t("home.solution.apiIntegration")}
								</span>
							</HStack>
							<p className="text-lg text-ct-regular font-medium whitespace-pre-line">
								{t("home.solution.description")}
							</p>
						</VStack>

						{/* 카테고리 뱃지 */}
						<VStack className="gap-[10px] items-start w-full">
							{categories.map((cat) => (
								<HStack key={cat.key} className="gap-[10px] items-center">
									<div className={`w-[8px] h-[8px] rounded-full ${categoryDotColors[cat.key]}`} />
									<span className="text-sm text-ct-regular font-medium">{cat.label}</span>
									<span className="text-xs text-ct-weak">
										({platforms.filter((p) => p.category === cat.key).length})
									</span>
								</HStack>
							))}
						</VStack>
					</VStack>
				</motion.div>

				{/* 모바일: 플랫폼 그리드 */}
				<div className="w-full lg:hidden">
					<div className="flex flex-wrap gap-[12px] justify-center">
						{platforms.map((item, i) => {
							const logo = platformLogos[item.name];
							return (
								<motion.div
									key={item.name}
									className="w-[48px] h-[48px] rounded-full bg-bg-weak border border-ct-weak/20 flex items-center justify-center shadow-sm"
									initial={{ opacity: 0, scale: 0.8 }}
									whileInView={{ opacity: 1, scale: 1 }}
									viewport={{ once: true }}
									transition={{ delay: i * 0.03, duration: 0.3 }}
								>
									{logo && (
										<svg width={24} height={24} viewBox={logo.viewBox} fill="none">
											{logo.elements.map((el, j) => (
												<path
													key={j}
													d={el.d}
													fill={el.fill ?? "none"}
													stroke={el.stroke}
													strokeWidth={el.strokeWidth}
													opacity={el.opacity}
													strokeLinecap={el.strokeLinecap as any}
													strokeLinejoin={el.strokeLinejoin as any}
												/>
											))}
										</svg>
									)}
								</motion.div>
							);
						})}
					</div>
				</div>

				{/* 데스크톱: 플랫폼 궤도 애니메이션 */}
				<motion.div
					className="hidden lg:flex flex-1 w-full min-h-[560px] relative"
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					{/* 궤도 원 (점선) + 연결선 */}
					<svg className="absolute inset-0 w-full h-full" viewBox="-300 -280 600 560">
						<ellipse
							cx="0"
							cy="0"
							rx="110"
							ry="100"
							fill="none"
							stroke="currentColor"
							strokeDasharray="4 6"
							className="text-ct-weak/20"
						/>
						<ellipse
							cx="0"
							cy="0"
							rx="185"
							ry="170"
							fill="none"
							stroke="currentColor"
							strokeDasharray="4 6"
							className="text-ct-weak/15"
						/>
						<ellipse
							cx="0"
							cy="0"
							rx="255"
							ry="235"
							fill="none"
							stroke="currentColor"
							strokeDasharray="4 6"
							className="text-ct-weak/10"
						/>
						{allItems.map((item) => (
							<line
								key={`line-${item.name}`}
								x1={0}
								y1={0}
								x2={item.x}
								y2={item.y}
								stroke="currentColor"
								strokeWidth="0.5"
								strokeDasharray="3 4"
								className="text-ct-weak/15"
							/>
						))}
					</svg>

					{/* 중앙 인플렉트 로고 */}
					<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
						<motion.div
							className="w-[64px] h-[64px] rounded-full bg-pm-strong flex items-center justify-center shadow-lg"
							animate={{ scale: [1, 1.08, 1] }}
							transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
						>
							<Icon name="Inflect" width={36} height={36} className="text-white" />
						</motion.div>
					</div>

					{/* 플랫폼 버블들 */}
					{allItems.map((item, i) => {
						const floatDuration = 3 + (i % 3);
						const size = item.orbit === 0 ? 44 : item.orbit === 1 ? 38 : 34;
						const imgSize = item.orbit === 0 ? 26 : item.orbit === 1 ? 22 : 18;
						const logo = platformLogos[item.name];
						const left = ((item.x + 300) / 600) * 100;
						const top = ((item.y + 280) / 560) * 100;

						return (
							<motion.div
								key={item.name}
								className="absolute -translate-x-1/2 -translate-y-1/2"
								style={{ left: `${left}%`, top: `${top}%` }}
								initial={{ opacity: 0 }}
								whileInView={{ opacity: 1 }}
								viewport={{ once: true }}
								transition={{ delay: 0.1 + i * 0.04, duration: 0.5 }}
							>
								<motion.div
									className="rounded-full bg-bg-weak border border-ct-weak/20 flex items-center justify-center shadow-lg"
									style={{ width: size, height: size }}
									animate={{ y: [-4, 4, -4] }}
									transition={{
										duration: floatDuration,
										delay: i * 0.3,
										repeat: Number.POSITIVE_INFINITY,
										ease: "easeInOut",
									}}
								>
									{logo && (
										<motion.svg
											width={imgSize}
											height={imgSize}
											viewBox={logo.viewBox}
											fill="none"
										>
											{logo.elements.map((el, j) => (
												<motion.path
													key={j}
													d={el.d}
													fill={el.fill ?? "none"}
													stroke={el.stroke}
													strokeWidth={el.strokeWidth}
													opacity={el.opacity}
													strokeLinecap={el.strokeLinecap}
													strokeLinejoin={el.strokeLinejoin}
												/>
											))}
										</motion.svg>
									)}
								</motion.div>
							</motion.div>
						);
					})}
				</motion.div>
			</HStack>
		</VStack>
	);
};
